// 首页（营销站范式）：Hero → 信任带 → 品类宫格（标题行内嵌搜索）→ 甄选商品 → 服务保障 → 页脚
// 移除旧「工作台」统计卡（假数据）与绿色 CTA 通栏；询盘入口收敛为 Hero CTA + 底部 Tab
import { Box, Typography, TextField, InputAdornment, Grid, Button, Skeleton, Alert } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import HeroBanner from '@/components/buyer/HeroBanner';
import TrustBand from '@/components/buyer/TrustBand';
import CategoryGrid from '@/components/buyer/CategoryGrid';
import ServiceBand from '@/components/buyer/ServiceBand';
import BuyerFooter from '@/components/buyer/BuyerFooter';
import ProductCard from '@/components/buyer/ProductCard';
import { productApi, type CatalogCategory, type BuyerSpuVO } from '@/api/productApi';
import { usePrefs } from '@/store/BuyerPrefsContext';
import { toDisplaySpus } from '@/utils/spuAdapter';

import { tr } from '@/i18n';
import { catalogLabel } from '@/i18n/catalog';
export default function Home() {
  const location = useLocation();
  const { t, lang } = usePrefs();
  const [records, setRecords] = useState<BuyerSpuVO[]>([]);
  const products = useMemo(() => toDisplaySpus(records, lang), [records, lang]);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [spuTotal, setSpuTotal] = useState(0);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let alive = true;
    productApi.publicCategories().then(data => { if (alive) setCategories(data); }).catch(() => undefined);
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (keyword.trim() === query) return;
    const timer = setTimeout(() => { setQuery(keyword.trim()); setPage(1); setRecords([]); }, 300);
    return () => clearTimeout(timer);
  }, [keyword, query]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFailed(false);
    productApi.listCards({ page, pageSize: 8, name: query || undefined, categoryId: activeCat || undefined })
      .then(result => {
        if (!alive) return;
        setRecords(previous => page === 1 ? result.records : [...previous, ...result.records]);
        setFilteredTotal(result.total);
        if (!query && !activeCat) setSpuTotal(result.total);
      })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [page, query, activeCat, retry]);

  const selectCategory = (id: string) => {
    if (id === activeCat) return;
    setActiveCat(id); setPage(1); setRecords([]);
  };

  // 顶导航/Hero「浏览商品」锚点：进入首页带 hash 时滚动到对应区块
  useEffect(() => {
    if (!location.hash) return;
    const timer = setTimeout(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
    }, 350);
    return () => clearTimeout(timer);
  }, [location.hash]);

  const cats = categories.map(c => ({ ...c, name: catalogLabel(lang, c.name, c.nameEn) }));
  const visible = products;

  return (
    <Box className="buyer-home" sx={{ overflowAnchor: 'none' }}>
      <Box className="buyer-masthead">
        <HeroBanner />
        <Box className="buyer-trust-glass"><TrustBand spuTotal={spuTotal} categoryCount={cats.length} /></Box>
      </Box>

      {/* 品类宫格：搜索并入区块标题行（标题左/搜索右），热门词与宫格功能重复故去掉 */}
      {(
        <Box id="categories" className="buyer-categories" sx={{ mt: 3.5 }}>
          <Typography sx={{ fontSize: { xs: '19px', md: '26px' }, fontWeight: 700, color: '#212121', mb: 1 }}>{t('home.categories')}</Typography>
          {/* 搜索独占一行（标题下/宫格上，与移动端同构），左对齐锚定宫格 */}
          <Box sx={{ maxWidth: { xs: '100%', md: 600 }, mb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('home.search')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              sx={{
                bgcolor: '#FFFFFF',
                borderRadius: '999px',
                '& .MuiOutlinedInput-root': { borderRadius: '999px', height: 44 },
                '& .MuiOutlinedInput-input': { py: 0.5, fontSize: '13px' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: '#9E9E9E' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <CategoryGrid categories={cats} activeId={activeCat} allLabel={t('home.allCats')} onSelect={selectCategory} />
        </Box>
      )}

      {/* 甄选农产品（overflowAnchor:none：打字过滤导致卡片区塌缩时，禁止浏览器滚动重锚定把视口甩走） */}
      <Box id="products" sx={{ mt: 3.5, overflowAnchor: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontSize: { xs: '19px', md: '26px' }, fontWeight: 700, color: '#212121' }}>{t('home.selected')}</Typography>
          {(keyword || activeCat) && (
            <Typography
              onClick={() => {
                setKeyword('');
                if (query || activeCat) {
                  setQuery(''); setActiveCat(''); setPage(1); setRecords([]);
                }
              }}
              sx={{ fontSize: '12px', color: '#059669', cursor: 'pointer' }}
            >
              {t('home.viewMore')}
            </Typography>
          )}
        </Box>
        <Grid container spacing={{ xs: 1.5, md: 2.5 }}>
          {visible.map((p) => (
            <Grid item xs={6} sm={4} md={3} key={p.id}>
              <ProductCard spu={p} />
            </Grid>
          ))}
          {!loading && !failed && visible.length === 0 && (
            <Typography sx={{ fontSize: '12px', color: '#9E9E9E', p: 2 }}>{t('home.noMatch')}</Typography>
          )}
          {loading && Array.from({ length: page === 1 ? 8 : 2 }, (_, i) => (
            <Grid item xs={6} sm={4} md={3} key={`loading-${i}`}>
              <Skeleton variant="rounded" height={225} />
            </Grid>
          ))}
        </Grid>
        {failed && <Alert severity="warning" sx={{ mt: 2 }} action={<Button onClick={() => setRetry(v => v + 1)}>{tr('home.retry')}</Button>}>
          {tr('home.unableToLoadProductsPlease')}
        </Alert>}
        {!loading && !failed && records.length < filteredTotal && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button variant="outlined" onClick={() => setPage(v => v + 1)}>{tr('home.loadMore')}</Button>
          </Box>
        )}
      </Box>

      {/* 服务与保障 */}
      <Box id="services" sx={{ mt: 4 }}>
        <ServiceBand />
      </Box>

      <BuyerFooter />
    </Box>
  );
}
